import { useEffect, useState } from "react";
import { Clock, BookOpen, ChevronRight } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import DashboardShell from "@/components/DashboardShell";
import { useAuth } from "@/context/AuthContext";

const CATS = ["All", "Business Growth", "Leadership", "Personal Branding", "Women Empowerment", "Skill Development"];

export default function Learning() {
  const { user } = useAuth();
  const [courses, setCourses] = useState([]);
  const [cat, setCat] = useState("All");

  useEffect(() => { api.get("/courses").then((r) => setCourses(r.data)).catch(() => {}); }, []);

  const filtered = cat === "All" ? courses : courses.filter((c) => c.category === cat);
  const role = user?.role === "admin" ? "admin" : "member";

  return (
    <DashboardShell role={role} title="Online Learning" subtitle="Cohort programs by women who have done it.">
      <div data-testid="learning-page">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-8">
            <p className="overline">Online Learning</p>
            <h1 className="font-display text-4xl md:text-5xl text-aubergine mt-4 leading-tight">
              Cohort programs. <em className="text-rose not-italic">Taught by women who've done it.</em>
            </h1>
          </div>
          <div className="lg:col-span-4 text-plumSoft font-light">
            Live sessions. Templates. Certificates. Lifetime community.
          </div>
        </div>

        <div className="mt-10 flex flex-wrap gap-2">
          {CATS.map((c) => (
            <button
              key={c}
              data-testid={`course-cat-${c.replace(/\s/g,'-').toLowerCase()}`}
              onClick={() => setCat(c)}
              className={`px-4 py-2 rounded-full text-xs uppercase tracking-widest border transition-all ${
                cat === c ? "bg-aubergine text-ivory border-aubergine" : "border-aubergine/20 text-plum hover:bg-aubergine/5"
              }`}
            >
              {c}
            </button>
          ))}
        </div>

        <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((c) => (
            <article key={c.id} data-testid={`course-card-${c.id}`} className="card-luxe overflow-hidden group flex flex-col">
              <div className="aspect-[16/10] overflow-hidden">
                <img src={c.image} alt={c.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
              </div>
              <div className="p-6 flex flex-col flex-1">
                <span className="overline">{c.category}</span>
                <h3 className="font-display text-2xl text-aubergine mt-2 leading-snug">{c.title}</h3>
                <p className="text-sm text-plumSoft mt-3 font-light leading-relaxed line-clamp-3">{c.description}</p>
                <div className="mt-5 flex flex-wrap gap-x-5 gap-y-1.5 text-xs text-plum">
                  <span className="flex items-center gap-1.5"><Clock className="w-3.5 h-3.5" /> {c.duration}</span>
                  <span className="flex items-center gap-1.5"><BookOpen className="w-3.5 h-3.5" /> {c.lessons} lessons</span>
                </div>
                <p className="mt-4 text-xs text-plumSoft">with {c.instructor}</p>
                <div className="mt-6 flex items-end justify-between pt-4 border-t border-aubergine/10">
                  <p className="font-display text-2xl text-aubergine">₹ {c.price.toLocaleString("en-IN")}</p>
                  <button
                    data-testid={`course-enroll-${c.id}`}
                    onClick={() => toast("Enrollment opens Phase 2", { description: "We'll email you when payments go live." })}
                    className="flex items-center gap-1 text-sm text-plum hover:gap-2 transition-all"
                  >
                    Enroll <ChevronRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      </div>
    </DashboardShell>
  );
}
